import React from 'react';

import {SeasonalMetricSet} from '../../lib/api/eval';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const LEAD_MONTHS = ['1', '2', '3', '6', '9', '12'];
const LEAD_LABELS: Record<string, string> = {
  '1': '1mo',
  '2': '2mo',
  '3': '3mo',
  '6': '6mo',
  '9': '9mo',
  '12': '12mo',
};

type MetricKey = 'kge' | 'nrmse' | 'mae' | 'percentBias';

function colorScale(metric: MetricKey, value: number): string {
  if (metric === 'kge') {
    // Higher is better: green (1) -> yellow (0.5) -> red (0)
    if (value >= 0.7) return '#16a34a';
    if (value >= 0.5) return '#65a30d';
    if (value >= 0.3) return '#ca8a04';
    if (value >= 0) return '#ea580c';
    return '#dc2626';
  }
  if (metric === 'nrmse') {
    // Lower is better
    if (value <= 0.3) return '#16a34a';
    if (value <= 0.5) return '#65a30d';
    if (value <= 0.7) return '#ca8a04';
    if (value <= 1.0) return '#ea580c';
    return '#dc2626';
  }
  if (metric === 'percentBias') {
    const abs = Math.abs(value);
    if (abs <= 10) return '#16a34a';
    if (abs <= 25) return '#ca8a04';
    return '#dc2626';
  }
  // MAE: scale-dependent, use neutral blue scale
  return '#3b82f6';
}

const MIN_COUNT = 3; // Minimum data points to show a cell as filled

const LABEL_W = 48;
const LABEL_H = 28;
const CELL_H = 32;

export default function MonthHorizonHeatmap({
  data,
  metric = 'kge',
}: {
  data?: Record<string, Record<string, SeasonalMetricSet>>;
  metric?: MetricKey;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(432);
  const [hoveredCell, setHoveredCell] = React.useState<{month: string; lead: string} | null>(null);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(Math.round(entry.contentRect.width));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!data) {
    return (
      <div className="text-sm text-gray-400 text-center py-8">
        No horizon x month data available
      </div>
    );
  }

  const cellW = (width - LABEL_W) / LEAD_MONTHS.length;
  const totalH = LABEL_H + 12 * CELL_H;

  return (
    <div ref={containerRef}>
      <svg viewBox={`0 0 ${width} ${totalH}`} width="100%" height={totalH}>
        {/* Column headers (lead months) */}
        {LEAD_MONTHS.map((lead, li) => (
          <text
            key={lead}
            x={LABEL_W + li * cellW + cellW / 2}
            y={LABEL_H - 6}
            textAnchor="middle"
            fontSize={10}
            fill="#6b7280"
            fontWeight="600"
          >
            {LEAD_LABELS[lead]}
          </text>
        ))}

        {/* Rows */}
        {Array.from({length: 12}, (_, mi) => {
          const monthKey = String(mi + 1);
          const monthData = data[monthKey] ?? {};

          return (
            <g key={monthKey}>
              {/* Row label */}
              <text
                x={LABEL_W - 6}
                y={LABEL_H + mi * CELL_H + CELL_H / 2}
                textAnchor="end"
                fontSize={10}
                fill="#374151"
                dy={3}
              >
                {MONTH_LABELS[mi]}
              </text>

              {/* Cells */}
              {LEAD_MONTHS.map((lead, li) => {
                const cell = monthData[lead];
                const raw = cell?.[metric];
                const value = raw != null && Number.isFinite(raw) ? raw : null;
                const count = cell?.forecastCount ?? 0;
                const hasData = value !== null && count >= MIN_COUNT;
                const isHov = hoveredCell?.month === monthKey && hoveredCell?.lead === lead;

                const x = LABEL_W + li * cellW;
                const y = LABEL_H + mi * CELL_H;

                return (
                  <g
                    key={lead}
                    onMouseEnter={() => setHoveredCell({month: monthKey, lead})}
                    onMouseLeave={() => setHoveredCell(null)}
                    style={{cursor: 'default'}}
                  >
                    <rect
                      x={x + 1}
                      y={y + 1}
                      width={cellW - 2}
                      height={CELL_H - 2}
                      rx={3}
                      fill={hasData ? colorScale(metric, value!) : '#f9fafb'}
                      fillOpacity={hasData ? (isHov ? 0.9 : 0.6) : 1}
                      stroke={isHov ? '#374151' : '#e5e7eb'}
                      strokeWidth={isHov ? 1.5 : 0.5}
                    />
                    {hasData && (
                      <text
                        x={x + cellW / 2}
                        y={y + CELL_H / 2}
                        textAnchor="middle"
                        fontSize={9}
                        fill="#1f2937"
                        dy={3}
                      >
                        {value!.toFixed(2)}
                      </text>
                    )}
                    {!hasData && count > 0 && (
                      <text
                        x={x + cellW / 2}
                        y={y + CELL_H / 2}
                        textAnchor="middle"
                        fontSize={8}
                        fill="#d1d5db"
                        dy={3}
                      >
                        n={count}
                      </text>
                    )}
                    {isHov && hasData && (
                      <title>
                        {MONTH_LABELS[mi]} @ {LEAD_LABELS[lead]} lead: {metric.toUpperCase()}=
                        {value!.toFixed(3)}, n={count}
                      </title>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Axis labels */}
        <text
          x={LABEL_W + (LEAD_MONTHS.length * cellW) / 2}
          y={LABEL_H - 18}
          textAnchor="middle"
          fontSize={11}
          fill="#6b7280"
        >
          Lead time to target month
        </text>
      </svg>
    </div>
  );
}
